<template>
  <div id="metadata-editor">
    <div class="metadata-editor-filter">
      <DxTextBox placeholder="Search..."
                v-model="metadataSearchText"
                :show-clear-button="true"
                 value-change-event="input"
      />
      <DxSelectBox class="metadata-visibility-dropdown"
                   v-model="visibilityMode"
                   :items="metadataVisibilityOptions"
      />
    </div>
    <DxForm class="metadata-editor-form"
            v-model:form-data="formData"
            :disabled="disabled"
            label-mode="floating"
            styling-mode="underlined"
            :show-colon-after-label="false"
    >
      <DxItem v-for="column in filteredColumns"
              :data-field="column.dataField"
      >
        <template #default>
          <component :is="getEditor(column).component"
                      v-model:value="formData[column.dataField]"
                      v-bind="getEditor(column).props"
          />
        </template>
      </DxItem>
    </DxForm>
    <div class="submit-button-container" v-if="!autosave">
      <DxButton :text="submitButtonText || 'Update Entire Basket'"
                :disabled="_.isEmpty(modifiedData)"
                type="action"
                width="100%"
                @click="applyMetadataChanges"
      />
      <DxButton icon="remove"
                :disabled="_.isEmpty(modifiedData)"
                @click="clearForm"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import _ from "lodash";
import { DxForm, DxItem } from "devextreme-vue/form";
import { DxTextBox } from "devextreme-vue/text-box";
import { DxNumberBox } from "devextreme-vue/number-box";
import { DxDateBox } from "devextreme-vue/date-box";
import { DxSelectBox, DxButton } from "devextreme-vue";
import { useCabinetStore } from "@/stores/cabinetStore.js";
import LookupDropdown from "@/components/base-components/LookupDropdown.vue";

const props = defineProps(["modeParams", "onSubmitMetadataEdits", "submitButtonText", "initialData", "autosave", "disabled"]);

const cabinetStore = useCabinetStore();
const { userColumnConfig, reservedColumns } = storeToRefs(cabinetStore);

const PRIORITY_METADATA_FIELDS = ["Doc Type", "State", "County"];
const VISIBLE_VISIBILITY_MODE = "Visible";
const DEFAULT_VISIBILITY_MODE = "All";
const metadataVisibilityOptions = [VISIBLE_VISIBILITY_MODE, DEFAULT_VISIBILITY_MODE];

const metadataSearchText = ref("");
const visibilityMode = ref(DEFAULT_VISIBILITY_MODE);
const formData = ref({ ...(props.initialData || {}) });
const modifiedData = computed(() => {
  const initial = props.initialData || {};
  return _.pickBy(formData.value, (value, key) => {
    const originalValue = initial[key];
    // Compare values, handling null, undefined, and empty strings
    return !_.isEqual(value, originalValue);
  });
});

// This is used to switch back and forth between Mass Edit Basket (which should show no data) and editing a single document (and showing that doc's metadata)
watch(() => props.initialData, (newData) => {
    formData.value = { ...(newData || {}) };
  }, { deep: true }
)

// Watch for changes in modifiedData and auto-submit if autosave is enabled
watch(modifiedData, (newModifiedData) => {
    if (props.autosave && !_.isEmpty(newModifiedData))
      applyMetadataChanges();
  },
  { deep: true }
)

// All the columns are available on the frontend but we filter it based on search and the visible/all dropdown
const filteredColumns = computed(() => {
  let filteredColumns = userColumnConfig.value;

  const readOnlyReservedFields = reservedColumns.value
    .filter((col) => col.allowEditing === false)
    .map((col) => col.dataField)
  filteredColumns = filteredColumns.filter(
    (col) => !readOnlyReservedFields.includes(col.dataField),
  )

  const filteredSearchText = simplifiedSearchText(metadataSearchText.value)
  filteredColumns = filteredColumns.filter((col) =>
    simplifiedSearchText(col.dataField).includes(filteredSearchText),
  )

  if (visibilityMode.value === VISIBLE_VISIBILITY_MODE)
    filteredColumns = filteredColumns.filter((col) => col.visible)

  return filteredColumns.toSorted((leftColumn, rightColumn) => {
    const leftPriority = PRIORITY_METADATA_FIELDS.indexOf(leftColumn.dataField)
    const rightPriority = PRIORITY_METADATA_FIELDS.indexOf(rightColumn.dataField)

    if (leftPriority !== -1 || rightPriority !== -1) {
      if (leftPriority === -1) return 1
      if (rightPriority === -1) return -1
      return leftPriority - rightPriority
    }

    return 0
  })
})

// For search filtering - takes in string and removes non alpha/num characters and lower cases it
function simplifiedSearchText(text) {
  return text.toLowerCase().replace(/[^a-z0-9]/, "")
}

const editors = {
  string: {
    component: DxTextBox,
    props: { stylingMode: "underlined", showClearButton: true },
  },
  number: {
    component: DxNumberBox,
    props: { stylingMode: "underlined", showClearButton: true },
  },
  boolean: {
    component: DxSelectBox,
    props: {
      stylingMode: "underlined",
      items: ["true", "false"],
      showClearButton: true,
    },
  },
  date: {
    component: DxDateBox,
    props: {
      stylingMode: "underlined",
      dateSerializationFormat: "yyyy-MM-dd",
      displayFormat: "yyyy/MM/dd",
      showClearButton: true,
    },
  },
  lookup: {
    component: LookupDropdown,
    props: {},
  },
};

function getEditor(column) {
  const editorConfig = editors[column.dataType]
  const dynamicProps = {}

  if (column.dataType === 'lookup')
    dynamicProps.columnName = column.dataField
  else
    dynamicProps.placeholder = column.caption || column.dataField

  return {
    component: editorConfig.component,
    props: { ...editorConfig.props, ...dynamicProps }
  }
}

const applyMetadataChanges = () => {
  props.onSubmitMetadataEdits(props.modeParams, modifiedData.value)
  clearForm()
}

const clearForm = () => ( formData.value = { ...(props.initialData || {}) } )
</script>

<style scoped lang="scss">
#metadata-editor {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  :deep(.dx-field-item) {
    padding: 0.5rem;
  }
}

.metadata-editor-form {
  background-color: white;
  border: 1px solid #e7e7e7;
  border-radius: 4px;

  :deep(.dx-box) > :nth-child(even) {
    background-color: #f5f5f5;
  }
}

.metadata-editor-filter {
  display: flex;
  gap: 0.5rem;

  .metadata-visibility-dropdown {
    flex: 0 0 100px;
  }
}

.submit-button-container {
  display: flex;
  gap: 0.5rem;
}
</style>
